import { useAuth } from '@/contexts/AuthContext'
import { resendVerificationEmailAction } from '@/features/email-verification/actions'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

const COOLDOWN_PERIOD_MS = 60 * 1000 // 60 seconds
const STORAGE_KEY_PREFIX = 'emailVerificationLastSend_'

export const useEmailVerification = () => {
  const { userData, currentUser, refreshUserData } = useAuth()
  const [isResending, setIsResending] = useState(false)
  const [hasCheckedForUpdates, setHasCheckedForUpdates] = useState(false)
  const [isInitialCheckComplete, setIsInitialCheckComplete] = useState(false)
  const [cooldownRemaining, setCooldownRemaining] = useState(0)

  const isEmailVerified = userData?.emailVerified || false
  const createdViaInvite = userData?.createdViaInvite || false
  const needsVerification = isInitialCheckComplete && currentUser && !isEmailVerified && !createdViaInvite

  // Get storage key for current user
  const storageKey = currentUser?.uid ? `${STORAGE_KEY_PREFIX}${currentUser.uid}` : null

  // Calculate cooldown remaining
  const calculateCooldownRemaining = useCallback((): number => {
    if (!storageKey) return 0

    try {
      const stored = localStorage.getItem(storageKey)
      if (!stored) return 0

      const lastSend = Number.parseInt(stored, 10)
      const elapsed = Date.now() - lastSend
      const remaining = COOLDOWN_PERIOD_MS - elapsed
      return Math.max(0, Math.ceil(remaining / 1000)) // Return seconds, rounded up
    } catch {
      return 0
    }
  }, [storageKey])

  // Save last send timestamp to localStorage
  const saveLastSendTimestamp = useCallback(
    (timestamp: number) => {
      if (!storageKey) return

      try {
        localStorage.setItem(storageKey, timestamp.toString())
      } catch {
        // Ignore localStorage errors
      }
    },
    [storageKey],
  )

  // Update cooldown countdown
  useEffect(() => {
    if (!currentUser) {
      setCooldownRemaining(0)
      return
    }

    // Initial calculation
    const remaining = calculateCooldownRemaining()
    setCooldownRemaining(remaining)

    // Update every second if cooldown is active
    if (remaining > 0) {
      const interval = setInterval(() => {
        const newRemaining = calculateCooldownRemaining()
        setCooldownRemaining(newRemaining)
        if (newRemaining === 0) {
          clearInterval(interval)
        }
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [currentUser, calculateCooldownRemaining])

  const canResend = cooldownRemaining === 0

  useEffect(() => {
    if (currentUser && !hasCheckedForUpdates) {
      setHasCheckedForUpdates(true)

      if (userData?.emailVerified) {
        setIsInitialCheckComplete(true)
        return
      }

      refreshUserData().finally(() => {
        setIsInitialCheckComplete(true)
      })
    } else if (!currentUser) {
      setHasCheckedForUpdates(false)
      setIsInitialCheckComplete(false)
    } else if (currentUser && userData !== null) {
      setIsInitialCheckComplete(true)
    }
  }, [currentUser, userData, hasCheckedForUpdates, refreshUserData])

  const resendVerificationEmail = async () => {
    if (!currentUser) {
      toast.error('Unable to resend verification email. Please try signing in again.')
      return false
    }

    if (isEmailVerified) {
      toast.info('Your email is already verified.')
      return true
    }

    if (createdViaInvite) {
      toast.info('Your email was verified when you accepted the team invitation.')
      return true
    }

    // Check cooldown
    if (!canResend) {
      toast.error(`Please wait ${cooldownRemaining} second${cooldownRemaining !== 1 ? 's' : ''} before resending.`)
      return false
    }

    setIsResending(true)
    try {
      const result = await resendVerificationEmailAction(currentUser.uid)

      if (!result.success) {
        throw new Error(result.error)
      }

      // Save timestamp after successful send
      saveLastSendTimestamp(Date.now())
      setCooldownRemaining(COOLDOWN_PERIOD_MS / 1000) // Set to 60 seconds

      toast.success('Verification email sent! Please check your inbox.')
      return true
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend verification email')
      return false
    } finally {
      setIsResending(false)
    }
  }

  const refreshVerificationStatus = async () => {
    await refreshUserData()
  }

  return {
    isEmailVerified,
    needsVerification,
    isResending,
    resendVerificationEmail,
    refreshVerificationStatus,
    isInitialCheckComplete,
    canResend,
    cooldownRemaining,
  }
}
